import {IActionable, IContextMenuActions} from "/@back@/commons/actionables.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {AgEltBoxSelection, IEltBoxSelection} from "/@back@/edit/wed/features/boxSel.js";
import {IWedFocusBarPointer} from "/@back@/edit/wed/features/commonBar.js";
import {AgEltBoxInsertDrawerBox} from "/@back@/edit/wed/features/insMgr.js";
import {IDataTransferWedAnalyzer, IWedMgrPointer, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {AgEltBoxInputAnnotable, IBoxInputDiffMaker, removeAnnots} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {TxtElement} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {EWedletEditMode, EWedletEditModeLabel, IChildWedlet, IElementWedlet, IParentWedlet, IWedlet, IWedletActionCtx, WEDLET, WedletActionCtxBase} from "/@back@/edit/wed/wedlets/wedlet.js";
import {OItemPointerInit, SrcPointer, SrcPointerCreateItem, SrcPointerDblClick, SrcPointerFastSelect} from "/@back@/wsp/widgets/srcDrawer.js";
import {IPtrItemRule} from "/@back@/wsp/widgets/wed/schemaMetaWsp.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IJmlNode, IJmlSubSet} from "/@lib@/commons/xml/jml.js";
import {IXmlMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {IShortDescCtx, IShortDescsTransfer, isShortDescsTransfer, ITEM, srcRefSub} from "/@lib@/wsp/item.js";
import {JSrcFields, SRC, srcRef} from "/@lib@/wsp/src.js";
import {WED, WedletModelBase} from "/@back@/edit/wed/wedCore.js";
import {IEltSpTeSource, SPTE, SpTeSourceUrlWedlet} from "/@back@/edit/wed/wedlets/spte/spte.js";
import {IWspUiEnv, JWspUriChangeMsg, WSP, wspCd} from "/@lib@/wsp/wsp.js";
import {DOM, ENodeType} from "/@lib@/commons/xml/dom.js";
import {ISkImporter, OSkImportDatas, OSkPasteContext} from "/@lib@/edit/schema/schemaMeta.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ITxtRootContext, TxtRoot} from "/@back@/edit/wed/wedlets/txt/txt.js";
import {AccelKeyMgr, ACTION, ActionHackCtx, IAction} from "/@lib@/commons/actions.js";
import {IInfoBroker} from "/@lib@/commons/infos.js";
import {FocusItem, ShortDescCopy} from "/@back@/wsp/actions/srcActions.js";
import {IRefreshHook} from "/@lib@/commons/utils/signboard.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IDiffAnnotValue} from "/@lib@/edit/schema/diff.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {EWspChangesEvts, IWspXmlHouse, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {LANG} from "/@lib@/commons/lang.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";

export interface BoxPtrItem {
	/** Sera implémenté par AgEltBoxSelection. */
	get ctxMenuActions(): IContextMenuActions<IWedletActionCtx & IShortDescCtx>;
}

export class BoxPtrItem extends SrcPointer implements IElementWedlet, IWedFocusBarPointer, IEltBoxSelection, IBoxInputDiffMaker {

	wedlet: IWedlet;

	selMode: 'box' | 'input' | 'caret';

	get wedMgr(): WedMgr {return this.wedlet.wedMgr}

	/** Optimisation */
	protected _pendingSrcRef: srcRef;
	protected _pendingFields: JSrcFields;


	/** On privillégie la IWedFocusBarPointer.focusBar du wed. */
	get focusActionables(): IActionable<IShortDescCtx>[] {return null}

	/**
	 * Pour l'obtention du bon contexte dans l'impl générique de focusBar fournit par AgEltBoxSelection()
	 * et dans SrcPointer. */
	get actionContext(): WedPtrItemCtx {return new WedPtrItemCtx(this)}

	/**
	 * Configuration d'un élément du wedlet à partir de son template issu du fichier wed.
	 */
	configWedletElt(tpl: Element, wedlet: IWedlet): void {
		this.wedlet = wedlet;
		if (this.hasAttribute("sel-mode")) this.selMode = this.getAttribute("sel-mode") as 'box' | 'input' | 'caret';
	}

	/**
	 * @param val IJmlNode pour les éléments, textes, comment; string pour la valeur d'un attribut.
	 * @param children utilisé uniquement par les élements des wedlets terminaux assumant la représentation de toute la descendance.
	 */
	refreshBindValue(val: IJmlNode | string, children?: IJmlSubSet): void {
		super.setSrcRef(val as srcRef, val === this._pendingSrcRef ? this._pendingFields : null);
		if (this.wedlet.isVirtual()) removeAnnots(this);
	}

	/** Appel par les actions en écriture. */
	setSrcRef(srcRef: srcRefSub, fields?: JSrcFields): void | Promise<void> {
		//super.setSrcRef(srcRef, fields); non !
		this._pendingSrcRef = srcRef;
		this._pendingFields = fields; //pour optimisation évite une requete au serveur.
		try {
			const docHolder = this.wedlet.wedMgr.docHolder;
			if (!docHolder.isAvailable) return; //house déconnectée entre temps.
			if (this.wedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay(this.wedlet, null, srcRef);
			} else {
				docHolder.newBatch().setAttr(this.wedlet.wedAnchor, srcRef).doBatch();
			}
		} finally {
			this._pendingSrcRef = null;
			this._pendingFields = null;
		}
	}

	replaceChars(chars: string, msg: IXmlMsg): void {
		super.setSrcRef(chars);
	}

	setEditMode(mode: EWedletEditMode) {
		this.readOnly = mode !== EWedletEditMode.write;
		DOM.setAttr(this, "tabindex", mode < EWedletEditMode.readStatic ? '0' : null);
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	isEmpty(): boolean {return this._srcRefSub != null}

	get sgnPattern(): RegExp {
		const docHolder = this.wedlet.wedMgr.docHolder;
		if (!docHolder) return null;
		const struct = docHolder.getStruct(this.wedlet.wedAnchor, WEDLET.buildVirtualPath(this.wedlet));
		//console.log("struct.itSgnPattern::::", struct ? (struct as IPtrItemRule).itSgnPattern : null);
		return (struct as IPtrItemRule)?.itSgnPattern;
	}

	get subSgnPattern(): RegExp {
		const docHolder = this.wedlet.wedMgr.docHolder;
		if (!docHolder) return null;
		const struct = docHolder.getStruct(this.wedlet.wedAnchor, WEDLET.buildVirtualPath(this.wedlet));
		//console.log("struct.itSgnPattern::::", struct ? (struct as IPtrItemRule).itSgnPattern : null);
		return (struct as IPtrItemRule)?.subItSgnPattern;
	}

	makeInputForDiff(annot: IDiffAnnotValue): HTMLElement {
		const ptr = new SrcPointer().initialize({reg: this.reg, readOnly: true, tpl: this.config.tpl, tplOver: this.config.tplOver});
		ptr.setSrcRef(annot.otherValue);
		return ptr;
	}

	handleEventFromContainer(ev: KeyboardEvent | MouseEvent) {
		if (!(ev instanceof KeyboardEvent) || ev.key === ' ' || ev.key === 'Enter') {
			if (!this.readOnly) {
				const ctxMenuActions = this.ctxMenuActions;
				const find = ctxMenuActions.actions.find((a) => a.getId() === "fastSelect");
				if (find) find.execute(ctxMenuActions.actionContext, ev);
				else {
					this.dispatchEvent(new CustomEvent("c-contextmenu", {bubbles: true, composed: true}));
				}
			}
			ev.preventDefault();
			ev.stopPropagation();
		}
	}
}

const ACTIONS = ["actions:wsp:wed:ptr-item"];
const ACCELKEYS = ["accelkeys:wsp:wed:ptr-item"];

AgEltBoxInsertDrawerBox(AgEltBoxSelection(AgEltBoxInputAnnotable(BoxPtrItem), {selMode: 'input', actionsLists: ACTIONS, groupOrderPref: "groupOrder.wsp.shortDesc", force_ctxMenuActions: true}));

export class BoxPtrItemBlock extends BoxPtrItem implements IEltBoxSelection {
	private defaultActionSvc?: string;

	protected _initialize(init: OItemPointerInit) {
		if (!this.reg) this.reg = this.findReg(init);
		if (!("defaultAction" in init)) init.defaultAction = this.reg.getSvc(this.defaultActionSvc || "ptrItemDblClick", SrcPointerDblClick.SINGLETON);
		if (!("actionsLists" in init)) init.actionsLists = ACTIONS;
		if (!("accelKeysLists" in init)) init.accelKeysLists = ACCELKEYS;
		if (!init.skinOver) init.skinOver = 'box-ptritem';
		if (this.classList.contains("compact")) {
			if (!init.tplSvc) init.tplSvc = "wedItemBlockCompact";
			if (!init.tplOverSvc) init.tplOverSvc = "wedItemBlockOverCompact";
		} else {
			if (!init.tplSvc) init.tplSvc = "wedItemBlock";
			if (!init.tplOverSvc) init.tplOverSvc = "wedItemBlockOver";
		}
		if (!("dispatchHighlight" in init)) init.dispatchHighlight = true;
		if (!("showStatus" in init)) init.showStatus = true;
		super._initialize(init);
	}

	buildInitFromAtts(init?: OItemPointerInit): OItemPointerInit {
		init = super.buildInitFromAtts(init);

		const defaultActionSvcAtt = this.getAttribute('default-action-svc') || this.getAttribute('defaultActionSvc');//TOTOsc7 : supprimer `defaultActionSvc`
		if (this.hasAttribute('defaultActionSvc')) console.warn("Attribute 'defaultActionSvc' is deprecated");
		if (defaultActionSvcAtt) this.defaultActionSvc = defaultActionSvcAtt;

		return init;
	}
}

REG.reg.registerSkin('box-ptritem', 1, /* language=CSS */ `
	:host {
		cursor: pointer;
		user-select: none;
		align-self: flex-start;
		padding-inline-end: 0;
	}

	:host(.previewLast) .itemPreview {
		order: 9999;
	}

	:host(:focus) {
		outline: var(--edit-input-focus);
	}

  :host(.empty[placeholder]):after {
	  content: attr(placeholder);
	  color: var(--fade-color);
	  overflow: hidden;
	  white-space: nowrap;
	  text-overflow: ellipsis;
  }

	wed-diff-mark {
		left: -5px;
	}
`);

window.customElements.define("box-ptritem", BoxPtrItemBlock);

export class BoxPtrItemInline extends BoxPtrItem implements IEltBoxSelection {
	private defaultActionSvc?: string;

	protected _initialize(init: OItemPointerInit) {
		if (!init.skin) init.skin = 'wsp-src-drawer-inline';
		if (!init.skinOver) init.skinOver = 'box-ptritem';
		if (!this.reg) this.reg = this.findReg(init);
		if (!("defaultAction" in init)) init.defaultAction = this.reg.getSvc(this.defaultActionSvc || "ptrItemDblClick", SrcPointerDblClick.SINGLETON);
		if (!("actionsLists" in init)) init.actionsLists = ACTIONS;
		if (!("accelKeysLists" in init)) init.accelKeysLists = ACCELKEYS;
		if (!init.tplSvc) init.tplSvc = "wedItemInline";
		if (!init.tplOverSvc) init.tplOverSvc = "wedItemInlineOver";
		if (!("dispatchHighlight" in init)) init.dispatchHighlight = true;
		if (!("showStatus" in init)) init.showStatus = true;
		super._initialize(init);
	}

	buildInitFromAtts(init?: OItemPointerInit): OItemPointerInit {
		init = super.buildInitFromAtts(init);

		const defaultActionSvcAtt = this.getAttribute('default-action-svc') || this.getAttribute('defaultActionSvc');//TOTOsc7 : supprimer `defaultActionSvc`
		if (this.hasAttribute('defaultActionSvc')) console.warn("Attribute 'defaultActionSvc' is deprecated");
		if (defaultActionSvcAtt) this.defaultActionSvc = defaultActionSvcAtt;

		return init;
	}
}

window.customElements.define("box-ptritem-inline", BoxPtrItemInline);


//*************************** Actions ******************************


export class Action4SrcPointer extends ActionHackCtx<IWedletActionCtx, SrcPointer> {
	wrapCtx(ctx: IWedletActionCtx): SrcPointer {
		return ctx.focusedElt as SrcPointer;
	}
}

function addAction(action: IAction<IWedletActionCtx>, ...accelKey: string[]) {
	REG.reg.addToList("actions:wsp:wed:ptr-item", action.getId(), 1, action);
	REG.reg.addToList("actions:wsp:wed:txt-ptr-item", action.getId(), 1, action);
	if (accelKey) for (const a of accelKey) {
		REG.reg.addToList("accelkeys:wsp:wed:ptr-item", a, 1, action);
		REG.reg.addToList("accelkeys:wsp:wed:txt-ptr-item", a, 1, action);//FIXME affiché mais pas pris en compte : events clavier gérés par le textRoot !
	}
}

addAction(new Action4SrcPointer(new FocusItem()), "F3");
addAction(new Action4SrcPointer(SrcPointerFastSelect.SINGLETON), "F4", "Enter", " ");
addAction(new Action4SrcPointer(new SrcPointerCreateItem().setGroup("ptrEdit")));

// Non, ajouté par la récupération de toutes les actions du TxtRoot.
// REG.reg.addToList("actions:wsp:wed:txt-ptr-item", "openMetaDialog", 1,
// 	new OpenMetaDialog().setLabel("Afficher/éditer les propriétés...").setIcon("/@skin@/edit/wed/txt/showMeta.svg")
// );

REG.reg.addToList("accelkeys:wsp:wed:ptr-item", "c-accel", 1, new Action4SrcPointer(ShortDescCopy.SINGLETON).setGroup("clipboard"));
//Dans le text (accelkeys:wsp:wed:txt-ptr-item) ctrl+C copie le fragment inline, ie le tag lien avec ses metas.
//Donc on ajoute une action dans le menu "Copier le lien".
REG.reg.addToList("actions:wsp:wed:txt-ptr-item", "copyLink", 1, new Action4SrcPointer(new ShortDescCopy("copyLink").setLabel("Copier le lien").setGroup("clipboard")), -1);


//*************************** Txt ******************************

const ACTIONS_TXT = ["actions:wsp:wed:txt-ptr-item"];
const ACCELKEYS_TXT = ["accelkeys:wsp:wed:txt-ptr-item"];

export class TxtPtrItem extends SrcPointer implements IWedMgrPointer, IWedFocusBarPointer, IRefreshHook {

	/** Action d'édition du lien. */
	editAction?: IAction<IShortDescCtx>

	/** Optimisation */
	protected _pendingSrcRef: srcRef;
	protected _pendingFields: JSrcFields;

	get txtElement(): TxtElement {return this.parentElement as TxtElement}

	get wedMgr(): WedMgr {return this.txtElement.wedMgr}

	get txtRoot(): TxtRoot {return this.txtElement.txtRoot}

	accelKeyMgr: AccelKeyMgr<WedTxtPtrItemCtx>;

	get actionContext(): WedTxtPtrItemCtx {return new WedTxtPtrItemCtx(this)}

	onRefreshCycle(before: boolean): 'stop' | void {return this.txtRoot.onRefreshCycle(before)}

	initSrcRef(srcRef: srcRef) {
		super.setSrcRef(srcRef);
	}

	/** Appel par les actions en écriture. */
	setSrcRef(srcRef: srcRefSub, fields?: JSrcFields): void | Promise<void> {
		//super.setSrcRef(srcRef, fields); non !
		const txtElt = this.txtElement;
		this._pendingSrcRef = srcRef;
		this._pendingFields = fields; //pour optimisation évite une requete au serveur.
		try {
			const docHolder = txtElt.wedMgr.docHolder;
			if (!docHolder.isAvailable) return; //house déconnectée entre temps.
			docHolder.newBatch().setAttr(XA.append(txtElt.wedAnchor, "sc:refUri"), srcRef).doBatch();
		} finally {
			this._pendingSrcRef = null;
			this._pendingFields = null;
		}
	}

	get sgnPattern(): RegExp {
		return sgnPatternFromTxtElement(this.txtElement);
	}

	get subSgnPattern(): RegExp {
		return sgnPatternFromTxtElement(this.txtElement, true);
	}

	/** On privillégie la focusBar du wed. */
	get focusActionables(): IActionable<IShortDescCtx>[] {return null}

	get focusBar(): BarActions<IWedletActionCtx> {
		if (this._focusBar === undefined) {
			const txtElt = this.txtElement;
			const wedMgr = txtElt.wedMgr;
			const datas = wedMgr.getDatasForModel(txtElt.model) as IWedFocusBarPointer;
			if ("focusBar" in datas) {
				this._focusBar = datas.focusBar;
			} else {
				const txtMenuActions = this.txtRoot.ctxMenuActions as IContextMenuActions<WedTxtPtrItemCtx>;
				const ctx = new WedTxtPtrItemCtx(this);
				let actions = txtMenuActions.actions.filter(a => a.getGroup(ctx) !== "clipboard").concat(...(this.reg.mergeLists<IAction<WedTxtPtrItemCtx>>(...this.actionsLists)));
				if (actions.length === 0) {
					this._focusBar = datas.focusBar = null;
				} else {
					actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", "open clipboard ptrEdit ins edit clone tools * importExport"), ctx);
					this._focusBar = datas.focusBar = new BarActions<IWedletActionCtx>().initialize({
						actions,
						actionContext: ctx
					});
				}
			}
		}
		this._focusBar.actionContext.focusedElt = this;
		return this._focusBar;
	}

	/** Interne, cache du focusBar construit. */
	_focusBar?: BarActions<IWedletActionCtx> | null;

	get ctxMenuActions(): IContextMenuActions<IShortDescCtx> {
		const txtMenuActions = this.txtRoot.ctxMenuActions as IContextMenuActions<WedTxtPtrItemCtx>;
		const ctx = new WedTxtPtrItemCtx(this);
		let actions = txtMenuActions.actions.concat(...this.reg.mergeLists<IAction<WedTxtPtrItemCtx>>(...this.actionsLists));
		actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", "open clipboard ptrEdit ins edit clone tools * importExport"), ctx);
		return actions && actions.length > 0 ? {actions, actionContext: ctx} : null;
	}

	protected _initDispatchHighlight(init: OItemPointerInit) {
		//Le focus est porté par le TxtElement parent, pas par ce TxtPtrItem.
		this.tabIndex = -1;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.config.dispatchHighlight) {
			const txtElt = this.txtElement;
			txtElt.addEventListener('focus', this.onFocus.bind(this));
			txtElt.addEventListener('blur', this.onBlur.bind(this));
		}
	}

	get readOnly(): boolean {return this.txtRoot.contentEditable !== 'true'}

	// disconnectedCallback() {
	// 	super.disconnectedCallback();
	// 	//XXX removeEventListener focus et blur ? Pas utile actuellement.
	// }

}

class WedPtrItemCtx extends WedletActionCtxBase implements IShortDescCtx {
	focusedElt: BoxPtrItem;

	get shortDescs(): JSrcFields[] {return this.focusedElt.shortDescs}

	get accelKeyMgr(): AccelKeyMgr<IShortDescCtx> {return this.focusedElt.accelKeyMgr}

	get reg(): IReg<IWspUiEnv> {return this.focusedElt.reg}

	get infoBroker(): IInfoBroker {return this.focusedElt.infoBroker}

	/** Ui d'appel, idéalement focusable pour reselection après dialogue. */
	get emitter(): HTMLElement {return this.focusedElt}
}

class WedTxtPtrItemCtx extends WedletActionCtxBase implements ITxtRootContext, IShortDescCtx {
	focusedElt: TxtPtrItem;

	get txtRoot(): TxtRoot {return this.focusedElt.txtRoot}

	get accelKeyMgr(): AccelKeyMgr<ITxtRootContext> {
		if (!this._accelKeyMgr) this._accelKeyMgr = new AccelKeyMgr().setAccelKeyMgrParent([this.focusedElt.accelKeyMgr, this.focusedElt.txtRoot.accelKeyMgr]);
		return this._accelKeyMgr;
	}

	protected _accelKeyMgr: AccelKeyMgr<ITxtRootContext>

	onRefreshCycle(before: boolean): 'stop' | void {return this.focusedElt.onRefreshCycle(before)}

	get shortDescs(): JSrcFields[] {return this.focusedElt.shortDescs}

	get reg(): IReg<IWspUiEnv> {return this.focusedElt.reg}

	get infoBroker(): IInfoBroker {return this.focusedElt.infoBroker}

	/** Ui d'appel, idéalement focusable pour reselection après dialogue. */
	get emitter(): HTMLElement {return this.focusedElt}
}

/** TxtBlock */
export class TxtPtrItemBlock extends TxtPtrItem {
	protected _initialize(init: OItemPointerInit) {
		if (!this.reg) this.reg = this.findReg(init);
		init.defaultAction = this.reg.getSvc("ptrItemDblClick", SrcPointerDblClick.SINGLETON);
		init.actionsLists = ACTIONS_TXT;
		init.accelKeysLists = ACCELKEYS_TXT;
		init.showStatus = true;
		super._initialize(init);
		this.editAction = this.reg.getSvc("ptrItemEditAction", SrcPointerFastSelect.SINGLETON);
	}
}

window.customElements.define("txt-ptritem", TxtPtrItemBlock);

/** InlObject et InlImg */
export class TxtPtrItemInline extends TxtPtrItem {
	protected _initialize(init: OItemPointerInit) {
		if (!init.skin) init.skin = 'wsp-src-drawer-inline';
		if (!init.skinOver) init.skinOver = 'inl-ptritem';
		if (!this.reg) this.reg = this.findReg(init);
		init.defaultAction = this.reg.getSvc("ptrItemDblClick", SrcPointerDblClick.SINGLETON);
		init.actionsLists = ACTIONS_TXT;
		init.accelKeysLists = ACCELKEYS_TXT;
		init.showStatus = true;
		super._initialize(init);
		this.editAction = this.reg.getSvc("ptrItemEditAction", SrcPointerFastSelect.SINGLETON);
	}
}

window.customElements.define("inl-ptritem", TxtPtrItemInline);

/** Surcharges du skin wsp-src-drawer-inline */
REG.reg.registerSkin('inl-ptritem', 1, /* language=CSS */ `
	:host {
		cursor: pointer;
	}

	:host(:focus) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	::selection {
		color: currentColor;
		background-color: var(--edit-seltext-bgcolor);
	}
`);


/** Recherche la signature des items autorisés d'un TxtElement de type lien (uLink, objectLeaf, extBlock, inlineImg)*/
export function sgnPatternFromTxtElement(txtElt: TxtElement, subItemSgn?: boolean): RegExp {
	const docHolder = txtElt.wedMgr.docHolder;
	if (!docHolder) return null;
	const path = txtElt.wedAnchor;
	path.push("sc:refUri");
	const struct = docHolder.getStruct(path, txtElt.wedAttributes && ("sc:refUri" in txtElt.wedAttributes) ? null : ["@"]);
	//console.log("struct.itSgnPattern::::", struct ? (struct as IPtrItemRule).itSgnPattern : null);
	return subItemSgn ? (struct as IPtrItemRule)?.subItSgnPattern : (struct as IPtrItemRule)?.itSgnPattern;
}


/**
 * SpTe editors : source du flux issu d'un ptrItem.
 */
export class SpTeSourceItemModel extends WedletModelBase {
	transform: string;

	initModel(config: Element): void {
		super.initModel(config);
		if (this.nodeType !== ENodeType.attribute) throw Error("SpTeSourceItem wedlet must be bind to an attribute");
		this.transform = config.getAttribute("transform");
	}

	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeSourceItemWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeSourceItem", SpTeSourceItemModel);

export class SpTeSourceItemWedlet extends SpTeSourceUrlWedlet {

	model: SpTeSourceItemModel;

	wspCd: wspCd;
	srcRef: srcRef;

	protected async initUrl(txt: string) {
		const spteElt = SPTE.findSpTeForkedWedlet(this).spTeViews[0].wedlet.spteSource;
		spteElt.setUrl(null);//reset avant de recharger la source.
		const reg = this.wedMgr.reg as IReg<IItemUiEnv>;
		this.wspCd = reg.env.wsp.code;
		this.srcRef = txt;
		if (txt) {
			if (!this._listenItem) reg.env.place?.eventsMgr.on("wspUriChange", (this._listenItem = this.listenItem.bind(this)));
			return this.refreshUrl(spteElt, reg);
		} else if (this._listenItem) {
			reg.env.place?.eventsMgr.removeListener("wspUriChange", this._listenItem);
			this._listenItem = undefined;
		}
	}

	protected async refreshUrl(spteElt: IEltSpTeSource, reg: IReg<IItemUiEnv>) {
		const wsp = reg.env.wsp;
		const shortDesc = await wsp.fetchShortDesc(this.srcRef, spteElt);
		if (shortDesc && shortDesc.itModel && shortDesc.srcSt > 0 && !document.wasDiscarded) {
			const srcUri = wsp.wspMetaUi.getItemType(shortDesc.itModel).getMainStreamSrcUri(shortDesc);
			spteElt.setUrl(WSP.getStreamUrl(wsp, srcUri, this.model.transform).url);
		}
	}

	protected listenItem(msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') {
		if (LANG.in(msg.type, EWspChangesEvts.u, EWspChangesEvts.r) && SRC.srcRef(msg) === this.srcRef && msg.wspCd === this.wspCd) {
			this.refreshUrl(SPTE.findSpTeForkedWedlet(this).spTeViews[0].wedlet.spteSource, this.wedMgr.reg as IReg<IItemUiEnv>);
		}
	}

	protected _listenItem?: (msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') => void
}

/**
 * SpTe editors : source d'un flux avec waveform issu d'un ptrItem.
 */
export class SpTeStreamItemModel extends SpTeSourceItemModel {
	createWedlet(parent: IParentWedlet): IChildWedlet {
		return new SpTeStreamItemWedlet(this, parent);
	}
}

WED.registerWedletModel("SpTeStreamItem", SpTeStreamItemModel);

export class SpTeStreamItemWedlet extends SpTeSourceItemWedlet {

	model: SpTeSourceItemModel;

	wspCd: wspCd;
	srcRef: srcRef;
	noCleanup: boolean;

	protected async initUrl(txt: string) {
		const currentRootMsg = this.wedMgr.currentMsgsStack[0];
		this.noCleanup = currentRootMsg && currentRootMsg.metas && currentRootMsg.metas.noCleanup;

		const spteElt = SPTE.findSpTeForkedWedlet(this).spTeViews[0].wedlet.spteSource;
		spteElt.setUrl(null);//reset avant de recharger la source.
		const reg = this.wedMgr.reg as IReg<IItemUiEnv>;
		this.wspCd = reg.env.wsp.code;
		this.srcRef = txt;

		spteElt.setUrl(null);//reset avant de recharger la source.
		if (txt) {
			if (!this._listenItem) reg.env.place?.eventsMgr.on("wspUriChange", (this._listenItem = this.listenItem.bind(this)));
			return this.refreshUrl(spteElt, reg);
		} else {
			reg.env.place?.eventsMgr.removeListener("wspUriChange", this._listenItem);
			this._listenItem = undefined;
		}
	}

	protected async refreshUrl(spteElt: IEltSpTeSource, reg: IReg<IItemUiEnv>) {
		const {wsp, universe} = this.wedMgr.reg.env;
		const shortDesc = await wsp.fetchShortDesc(this.srcRef, spteElt);
		if (shortDesc && shortDesc.itModel && shortDesc.srcSt > 0 && !document.wasDiscarded) {
			const srcUri = wsp.wspMetaUi.getItemType(shortDesc.itModel).getMainStreamSrcUri(shortDesc);

			let url: string;
			let waveformUrl: string;
			let duration: number;

			try {
				if (shortDesc.itModel.startsWith('sfile_')) {
					url = WSP.getStreamUrl(wsp, srcUri, this.model.transform).url;
					// Ressource binaire locale
					if ('setWaveformUrl' in spteElt) waveformUrl = WSP.getStreamUrl(wsp, srcUri, 'transform=ffmpeg&f=data&map=0:a&acodec=pcm_u8&ar=8k&ac=1&transform=waveformData&ar=8k&r=100').url;
					if ('setDurationProp' in spteElt) {
						try {
							const props = await WSP.getStreamUrl(wsp, srcUri, 'transform=ffmpeg&outType=PROPS&outFormat=json').fetchJson() as { duration: number };
							duration = props.duration;
						} catch (e) {
							await ERROR.log("Unable to retrieve the stream duration from properties");
						}
					}

				} else {
					// Ressource distante
					const resp = await universe.httpFrames.web.fetchSvc("*u/remoteContent");
					const remoteContentFacet = "transform=facet&facet=remoteContent";

					// Test de l'existance du service remoteContent
					if (resp.ok) {
						url = await WSP.fetchStreamText(wsp, this.wedMgr.wedEditor, srcUri, `${remoteContentFacet}&outType=LOCATION&queryString=scDepot:(gotoTags\'lowest\')`);
						if (url && 'setWaveformUrl' in spteElt) waveformUrl = await WSP.fetchStreamText(wsp, this.wedMgr.wedEditor, srcUri, `${remoteContentFacet}&outType=LOCATION&scDepotView=waveformData`);
						if (url && 'setDurationProp' in spteElt) {
							try {
								const props = await WSP.getStreamUrl(wsp, srcUri, `${remoteContentFacet}&outType=CONTENT&scDepotView=originalMetas`).fetchJson() as { duration: number };
								duration = props.duration;
							} catch (e) {
								await ERROR.log("Unable to retrieve the stream duration from properties");
							}

						}
					}
				}
			} catch (e) {
				await ERROR.report("Impossible de récupérer l'URL du flux ou de sa forme d'onde.", e);
			}

			if (url) {
				spteElt.setUrl(url, this.noCleanup);
				if (waveformUrl) spteElt.setWaveformUrl(waveformUrl);
				if (duration) spteElt.setDurationProp(duration);
			}

		}
	}

	protected listenItem(msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') {
		if (LANG.in(msg.type, EWspChangesEvts.u, EWspChangesEvts.r) && SRC.srcRef(msg) === this.srcRef && msg.wspCd === this.wspCd) {
			this.noCleanup = false;
			this.refreshUrl(SPTE.findSpTeForkedWedlet(this).spTeViews[0].wedlet.spteSource, this.wedMgr.reg as IReg<IItemUiEnv>);
		}
	}

	protected _listenItem?: (msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') => void
}

export const ptrItemTagsDefined = Promise.resolve();


export class WspDataTransferWedAnalyzer implements IDataTransferWedAnalyzer {

	static SINGLETON = new WspDataTransferWedAnalyzer();

	async tryPaste(clipboardItems: ClipboardItem[], pasteCtx: OSkPasteContext, cache: OSkImportDatas, wedMgr: WedMgr): Promise<ISkImporter[] | null> {
		//Sources externes
		const sdFromExt = await ITEM.getShortDescsTransferFromExternalClipboard(wedMgr.reg, clipboardItems, {targetSrcType: "item", canCreate: true}, wedMgr.wedEditor.rootNode);
		if (sdFromExt) {
			if (sdFromExt.extractDatasNow && await sdFromExt.extractDatasNow() === "stop") return null;
			const clipboard = clipboardItems[0];
			if (!cache.originalText && clipboard.types.indexOf("text/plain") >= 0) cache.originalText = await CLIPBOARD.text(clipboard, "text/plain");
			return this.tryPasteLinks(sdFromExt, cache, wedMgr, pasteCtx);
		}
		//Sources internes issus de l'application.
		const sdFromInt = await ITEM.getShortDescsTransferFromClipboardItems(clipboardItems, wedMgr.reg.env.wsp, wedMgr.wedEditor);
		if (isShortDescsTransfer(sdFromInt)) return wedMgr.docHolder.house.schemaDom.tryPasteLinks(pasteCtx, sdFromInt, cache);
		return null; //On laisse le comportement par défaut.
	}

	async tryPasteFiles(dataTrsf: DataTransfer, pasteCtx: OSkPasteContext, cache: OSkImportDatas, wedMgr: WedMgr): Promise<ISkImporter[] | null> {
		//Sources externes
		const sdFromExt = ITEM.getShortDescsTransferFromExternal(wedMgr.reg, dataTrsf, {targetSrcType: "item", canCreate: true}, wedMgr.wedEditor.rootNode);
		if (sdFromExt) {
			if (!cache.originalText && dataTrsf.types.indexOf("text/plain") >= 0) cache.originalText = dataTrsf.getData("text/plain");
			if (sdFromExt.extractDatasNow && await sdFromExt.extractDatasNow(dataTrsf) === "stop") return null;
			return this.tryPasteLinks(sdFromExt, cache, wedMgr, pasteCtx);
		}
		//Sources internes issus de l'application.
		const sdFromInt = await ITEM.getShortDescsTransferFromDataTransfer(dataTrsf, wedMgr.reg.env.wsp);
		if (sdFromInt) return wedMgr.docHolder.house.schemaDom.tryPasteLinks(pasteCtx, sdFromInt, cache);
	}

	extractDatasFromDragSession(this: void, dataTrsf: DataTransfer, wedMgr: WedMgr): OSkImportDatas {
		const shortDescTrsf = ITEM.getShortDescsTransferFromDragSession(wedMgr.reg, dataTrsf, {targetSrcType: "item", canCreate: true}, wedMgr.wedEditor);
		if (shortDescTrsf) {
			if (!shortDescTrsf.isImport && shortDescTrsf.srcRefs && shortDescTrsf.srcRefs.length === 0) return {};
			if (shortDescTrsf.server && wedMgr.reg.env.universe.config.universeUrl.url !== shortDescTrsf.server) {
				POPUP.showNotifInfo("Il est impossible d'exploiter des items d'un autre entrepôt.", wedMgr.wedEditor);
				return {};
			}
			return {originalLinks: shortDescTrsf};
		}
		return null;
	}

	private async tryPasteLinks(shortDescsTrsf: IShortDescsTransfer, cache: OSkImportDatas, wedMgr: WedMgr, pasteCtx: OSkPasteContext): Promise<ISkImporter[] | null> {
		const imps = await wedMgr.docHolder.house.schemaDom.tryPasteLinks(pasteCtx, shortDescsTrsf, cache);
		if (cache.originalText && (!imps || imps.length == 0)) {
			//Pas de lien trouvé sous forme de lien, mais on a une forme texte pure, on tente cette forme.
			return wedMgr.docHolder.house.schemaDom.tryPasteText(pasteCtx, cache.originalText, cache);
		}
		return imps;
	}
}